import { ActionTree } from 'vuex';
import moment from 'moment';
import { EFeatureFlag } from '../enums/feature-flag';
import { FeatureFlagService } from '../services/feature-flag.service';
import { BetaFlagHelper } from '../helpers/beta-flag.helper';
import { IBetaFlagState, IFlags } from '@/beta-flag/store/beta-flag.types';
import { EBetaFlagAction, EBetaFlagMutation } from '@/beta-flag/store/beta-flag.constants';
import { RootState } from '@/store/store';
import { BetaFlagService } from '@/services/beta-flag.service';
import { translate } from '@/helpers/translation.helper';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';

export const actions: ActionTree<IBetaFlagState, RootState> = {
  async [EBetaFlagAction.FetchBetaFlags]({ state, commit }) {
    const expires: moment.Moment = moment(state.dismissedBetaExpires);
    try {
      const [
        engagementBlocks,
        dealscore,
        contentLibrary,
        editNonSignableAfterSend,
        globalNavigation,
        editorInputField,
      ] = await Promise.all([
        BetaFlagService.fetchBetaFlag('engagementBlocks'),
        BetaFlagService.fetchBetaFlag('dealscore'),
        FeatureFlagService.evaluateFlag(EFeatureFlag.ContentLibrary),
        FeatureFlagService.evaluateFlag(EFeatureFlag.EditNonSignableAfterSent),
        FeatureFlagService.evaluateFlag(EFeatureFlag.GlobalNavigation),
        FeatureFlagService.evaluateFlag(EFeatureFlag.EditorInputField),
      ]);

      if (moment().isAfter(expires)) {
        commit(EBetaFlagMutation.SetDismissedBetaExpires, null);
      }
      const flags: IFlags = {
        engagementBlocks,
        contentLibrary,
        dealscore,
        editNonSignableAfterSend,
        globalNavigation,
        editorInputField,
      };
      commit(EBetaFlagMutation.SetFlags, flags);
    } catch (e) {
      console.error(e);
    }
  },
  async [EBetaFlagAction.FetchContentLibraryFlag]({ commit }) {
    try {
      const contentLibrary: number = await FeatureFlagService.evaluateFlag(
        EFeatureFlag.ContentLibrary
      );
      commit(EBetaFlagMutation.SetContentLibraryFlag, contentLibrary);
    } catch (e) {
      console.error(e);
    }
  },
  async [EBetaFlagAction.FetchDealscoreFlag]({ commit }) {
    try {
      const dealscore: number = await BetaFlagService.fetchBetaFlag('dealscore');
      commit(EBetaFlagMutation.SetDealscoreFlag, dealscore);
    } catch (e) {
      console.error(e);
    }
  },
  async [EBetaFlagAction.FetchEditNonSignableAfterSendFlag]({ commit }) {
    try {
      const editNonSignableAfterSend: number = await FeatureFlagService.evaluateFlag(
        EFeatureFlag.EditNonSignableAfterSent
      );
      commit(EBetaFlagMutation.SetEditNonSignableAfterSendFlag, editNonSignableAfterSend);
    } catch (e) {
      console.error(e);
    }
  },
  async [EBetaFlagAction.FetchGlobalNavFlag]({ commit }) {
    try {
      const globalNavigation: number = await FeatureFlagService.evaluateFlag(
        EFeatureFlag.GlobalNavigation
      );
      commit(EBetaFlagMutation.SetGlobalNavFlag, globalNavigation);
    } catch (e) {
      console.error(e);
    }
  },
  async [EBetaFlagAction.HandleBetaOptIn]({ dispatch }, feature: EFeatureFlag) {
    try {
      await FeatureFlagService.allowFeature(feature);
      dispatch(EBetaFlagAction.HandleDismissOptIn, feature);
      dispatch(EBetaFlagAction.FetchBetaFlags);
    } catch {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-save-the-setting-try-refreshing-the-page'),
        {
          root: true,
        }
      );
    }
  },
  async [EBetaFlagAction.HandleDismissOptIn]({ rootState }, feature: EFeatureFlag) {
    if (feature === EFeatureFlag.ContentLibrary) {
      const { session } = rootState['session'];
      BetaFlagHelper.dismissContentLibraryOptIn(
        session.entity_hash,
        session.user_hash,
        session.user_role
      );
    }
  },
};
